import { FunctionComponent, PropsWithChildren, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { AnalyticsEvent, logAnalyticsEvent } from '@vestico/utils';

import {
  ProductWidget,
  UserModel,
  getUserFromStorage,
  getUserGenderFromProduct,
  resetUser,
  userDataCompleted,
  writeUserToStorage,
} from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { ConfigContext } from './WidgetEventsManager';

export interface UserStateContextProps {
  updateUserUID: (uid: string) => void;
  didUpdateProduct: (product: ProductWidget | undefined | null) => void;
}

export const UserStateContext = createContext({} as UserStateContextProps);

export const UserStateProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<UserModel | null>(getUserFromStorage());
  const [didInitUser, setDidInitUser] = useState(false);
  const [product, setProduct] = useState<ProductWidget | undefined | null>(undefined);
  const { clientId } = useContext(ConfigContext);

  const [userUID, setUserUID] = useState(user?.uid);

  useLayoutEffect(() => {
    if (user) {
      return;
    }
    const storageUser = getUserFromStorage();
    setUser(storageUser);
    setUserUID(storageUser.uid);
  }, [user]);

  useEffect(() => {
    if (!user || didInitUser) {
      return;
    }
    setDidInitUser(true);
    updateGenderFromProduct(false);
  }, [user]);

  useEffect(() => {
    updateGenderFromProduct(true);
  }, [product]);

  const updateGenderFromProduct = useCallback(
    (resetUserObject: boolean) => {
      if (product && user && !userDataCompleted(user, product.matchingCategory, false, true)) {
        const newGender = getUserGenderFromProduct(product);
        if (newGender !== user.gender) {
          user.gender = newGender;
          if (resetUserObject) {
            setUser({ ...user });
          }
        }
      }
    },
    [product, user]
  );

  const didUpdateProduct = useCallback(
    (newProduct: ProductWidget) => {
      setProduct(newProduct);
    },
    [setProduct]
  );

  const reloadUser = useCallback(
    (isReset: boolean) => {
      if (isReset) {
        console.debug('reset');
        logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.reset, dimensions: { provider: clientId } });
        const newUser = resetUser(user);

        if (product) {
          const newGender = getUserGenderFromProduct(product);
          newUser.gender = newGender;
        }

        setUser(newUser);
        setUserUID(newUser.uid);
      } else {
        console.debug('reload');
        const newUser = getUserFromStorage();
        setUser(newUser);
        setUserUID(newUser.uid);
      }
    },
    [clientId, user]
  );

  const updateUserUIDIfNeeded = useCallback(
    (uid: string) => {
      if (!user) {
        return;
      }

      if (user.uid && user.uid === uid) {
        return;
      }

      if (user.uid && user.uid !== uid) {
        console.info(`Overwriting user uid from ${user.uid} to ${uid}`);
      }

      user.uid = uid;
      setUserUID(uid);
      writeUserToStorage(user);
    },
    [user]
  );

  const userStateContextValue = useMemo(() => {
    return { updateUserUID: updateUserUIDIfNeeded, didUpdateProduct };
  }, [updateUserUIDIfNeeded, didUpdateProduct]);

  const userContextValue = useMemo(() => {
    return { user, uid: userUID, reloadUser };
  }, [user, userUID, reloadUser]);

  return (
    <UserStateContext.Provider value={userStateContextValue}>
      <UserContext.Provider value={userContextValue}>{children}</UserContext.Provider>
    </UserStateContext.Provider>
  );
};
